/**
 * =============================================================================
 * navRegistry — Registro único da Navegação 2.0 (rail + painel de submenus)
 * Versão: 1.12.0 (2026-10-19)
 *
 * CHANGELOG:
 *   v1.12.0 (2026-10-19) - O hub geral de Dashboards passa a incluir "O.S."
 *     (Dashboard de Ordem de Serviço), reaproveitando a flag do módulo
 *     serviceOrders e a permissão já aplicada ao menu próprio.
 *   v1.11.0 (2026-10-10) - Ordem de Serviço ganha atalhos dinâmicos por
 *     categoria no Kanban; Gestão de Chip passa ao fim de Gestão Rastreador.
 *   v1.10.0 (2026-10-05) - Configurações passa a separar Organização,
 *     Acesso e atendimento, Preferências do sistema e Modelos e dados,
 *     preservando rotas, permissões e o grupo retrátil de Inteligência de Dados.
 *   v1.9.0 (2026-10-05) - Retenção e Jurídico passam a ter a mesma seção final
 *     de atalhos por categoria do Kanban, usando configuração compartilhada.
 *   v1.8.0 (2026-10-05) - O Kanban de Tarefas sai do bloco principal e passa
 *     para uma seção final com atalhos dinâmicos das categorias ativas.
 *   v1.7.0 (2026-10-03) - O hub geral de Dashboards passa a incluir Retenção,
 *     Jurídico e Veículos, reaproveitando as mesmas flags de módulo e
 *     permissões já aplicadas nos menus próprios e nas rotas protegidas.
 *   v1.6.0 (2026-09-17) - "Playbook de Vendas" e a categoria "Clientes" passam a
 *     exigir o módulo CRM (`requiresModule: 'crm'`), evitando menus visíveis
 *     para tenants sem o módulo contratado.
 *   v1.5.0 (2026-08-25) - CRM: grupo "Propostas" substituído pelo item
 *     "Propostas Geradas" (acima de Contratos Gerados) e criação da seção
 *     "Configurações" via novo campo `sectionTitle`.
 *
 * Descrição:
 *   Fonte única da árvore de navegação lateral do tenant. Cada categoria
 *   representa um item do "rail" (nível 1) e seus `children` são exibidos no
 *   painel lateral (nível 2), no estilo Pipedrive.
 *
 * IMPORTANTE (não quebrar nada):
 *   - As URLs e as `menuKey` foram COPIADAS das sidebars atuais
 *     (TenantSidebar, CrmSidebar, TasksSidebar, ActivitiesSidebar,
 *      FinancialSidebar, ReferralSidebar). Nenhuma rota ou permissão muda.
 *   - `menuKey: null` significa item liberado para todos (ex.: Templates).
 *   - `moduleType` dispara `switchModule()` do useActiveModule ao clicar,
 *     preservando o comportamento de "última rota por módulo".
 *   - `requiresModule` esconde a categoria quando a flag do módulo está OFF.
 * =============================================================================
 */
import {
  FileDown,
  LayoutDashboard,
  MessagesSquare,
  Briefcase,
  Contact,
  Users,
  Megaphone,
  CalendarDays,
  ListChecks,
  ActivitySquare,
  AlertOctagon,
  DollarSign,
  Handshake,
  BookOpen,
  Workflow,
  FileSpreadsheet,
  Settings,
  Kanban,
  ClipboardCheck,
  ListTodo,
  List,

  CalendarClock,
  MessageSquare,
  LayoutTemplate,
  Bell,
  Network,
  Route,
  Ban,
  GitBranch,
  Building2,
  CreditCard,
  Shield,
  Layers,
  FolderTree,
  Package,
  Target,
  UserCheck,
  FileSignature,
  FileCheck,
  XCircle,
  FileText,
  Columns,
  Tags,
  Upload,
  Globe,
  Car,
  MapPin,
  HeartHandshake,
  Scale,
  Database,
  Plug,
  History,
  Search,

  type LucideIcon,
  Rocket,
  ClipboardList,
  Boxes,
  Cpu,
  Truck,
  Factory,
  Wrench,
  Store,
  BadgeCheck,
  Monitor,
} from 'lucide-react';
import type { ModuleType } from '@/hooks/useActiveModule';
import type { TaskModuleType } from '@/hooks/useTaskModuleType';

/** Flags de módulo que podem condicionar a exibição de uma categoria. */
export type NavModuleFlag =
  | 'crm'
  | 'financial'
  | 'tasks'
  | 'referral'
  | 'activities'
  | 'knowledge'
  | 'vehicles'
  // Submódulos de Tarefas (flags próprias do tenant: retention_enabled / legal_enabled)
  | 'retention'
  | 'legal'
  // Módulo Workflow (esteira comercial): flag `workflow_enabled` do tenant
  | 'workflow'
  // Módulo Checklist (Template de Checklist + Vistoria): flag `checklist_enabled`
  | 'checklist'
  // 2026-10-06: Módulo Estoque (rastreadores e chips): flag `stock_enabled`
  | 'stock'
  // 2026-10-08: Módulo Ordem de Serviço: flag `service_orders_enabled`
  | 'serviceOrders';


/** Item de nível 2 (painel) — pode agrupar itens de nível 3. */
export interface NavItem {
  /** Chave i18n (namespace `sidebar`). */
  titleKey: string;
  /** Rótulo padrão caso a chave i18n não exista. */
  defaultLabel: string;
  /**
   * Rota de destino (idêntica à sidebar legada).
   * Opcional quando o item é apenas um GRUPO expansível (usa `children`).
   */
  url?: string;
  /** Chave de permissão (`menu_key`). `null` = liberado para todos. */
  menuKey: string | null;
  /** Flag de módulo obrigatória para o ITEM aparecer (mesma regra da categoria). */
  requiresModule?: NavModuleFlag;
  icon?: LucideIcon;
  /** Nível 3: subitens exibidos ao expandir a setinha do grupo. */
  children?: NavItem[];
  /**
   * v1.5.0 — Quando preenchido, um título de seção (não clicável) é exibido
   * ACIMA deste item no painel, agrupando visualmente os itens seguintes.
   */
  sectionTitle?: string;

}


/** Categoria de nível 1 (rail). */
export interface NavCategory {
  id: string;
  titleKey: string;
  defaultLabel: string;
  icon: LucideIcon;
  /** Quando não há filhos, o clique navega direto para esta URL. */
  url?: string;
  /** Permissão da própria categoria (quando é link direto). */
  menuKey?: string | null;
  /** Módulo ativado ao clicar (useActiveModule). */
  moduleType?: ModuleType;
  /** Flag de módulo obrigatória para a categoria aparecer. */
  requiresModule?: NavModuleFlag;
  /**
   * v1.6.0 — Alternativa ao `requiresModule`: a categoria aparece quando AO
   * MENOS UMA das flags listadas está ativa. Usado por telas compartilhadas
   * entre módulos (ex.: Clientes, usado por CRM, Financeiro, Retenção,
   * Jurídico e Veículos).
   */
  requiresAnyModule?: NavModuleFlag[];
  /** Prefixos de rota que marcam esta categoria como ativa. */
  matchPrefixes?: string[];
  /**
   * Quando true, a categoria ativa é resolvida SOMENTE por `matchPrefixes`/`url`,
   * ignorando as URLs dos filhos. Usado no hub "Dashboard", que agrega rotas
   * pertencentes a outras categorias (ex.: /tenant/financial/dashboard).
   */
  matchOnlyOwnPrefixes?: boolean;
  /** Seção especial renderizada no painel (árvores dinâmicas de navegação). */
  specialSection?: 'crmWorkspaces' | 'taskCategories' | 'serviceOrderCategories';
  /**
   * v1.3.0 — Permissão (menu_key) exigida para exibir a seção especial.
   * Sem ela, a seção some (e a categoria some se não sobrar nenhum filho).
   */
  specialSectionMenuKey?: string;
  /** v1.3.0 — Flag de módulo exigida para exibir a seção especial. */
  specialSectionModule?: NavModuleFlag;
  /** Tipo de tarefa usado pela seção dinâmica de categorias do Kanban. */
  specialSectionTaskType?: TaskModuleType;
  children?: NavItem[];
}

/**
 * Definições das categorias (a ordem de exibição no rail é controlada por
 * RAIL_ORDER, logo abaixo — assim reordenar o menu não move blocos de código).
 */
const NAV_CATEGORIES_DEFS: NavCategory[] = [
  // --------------------------------------------------------------------- Chat
  // Categoria direta para a tela principal de chat. Fica no topo do rail,
  // acima do Dashboard, e navega diretamente sem abrir painel.
  {
    id: 'chat',
    titleKey: 'nav.categories.chat',
    defaultLabel: 'Chat',
    icon: MessagesSquare,
    url: '/tenant/chat',
    menuKey: 'chat',
    matchPrefixes: ['/tenant/chat'],
  },

  // ---------------------------------------------------------------- Dashboard
  // Hub que reúne, em um só lugar, TODOS os dashboards do sistema. Os
  // dashboards continuam existindo dentro de cada módulo (nada foi removido);
  // aqui apenas replicamos os mesmos links, permissões e flags de módulo.
  {
    id: 'dashboard',
    titleKey: 'tenant.items.dashboard',
    defaultLabel: 'Dashboard',
    icon: LayoutDashboard,
    matchPrefixes: ['/tenant'],
    matchOnlyOwnPrefixes: true,
    children: [
      // Dashboard de Conversas (rota inicial /tenant) — restaurado: havia sido
      // removido da lista, ficando sem nenhum ponto de acesso no menu.
      { titleKey: 'tenant.items.conversas', defaultLabel: 'Conversas', url: '/tenant', menuKey: 'dashboard', icon: MessagesSquare },
      { titleKey: 'nav.categories.deals', defaultLabel: 'CRM', url: '/tenant/crm', menuKey: 'crm_dashboard', requiresModule: 'crm', icon: Briefcase },
      { titleKey: 'crm.items.activities', defaultLabel: 'Atividades', url: '/tenant/activities/dashboard', menuKey: 'activities_list', requiresModule: 'activities', icon: ActivitySquare },
      { titleKey: 'tasks.title', defaultLabel: 'Tarefas', url: '/tenant/tasks/dashboard', menuKey: 'tasks_dashboard', requiresModule: 'tasks', icon: ListChecks },
      { titleKey: 'admin.items.financeiro', defaultLabel: 'Financeiro', url: '/tenant/financial/dashboard', menuKey: 'financial_dashboard', requiresModule: 'financial', icon: DollarSign },
      { titleKey: 'nav.categories.referral', defaultLabel: 'Indicações', url: '/tenant/indicacao', menuKey: 'referral_dashboard', requiresModule: 'referral', icon: Handshake },
      // Mantém paridade com os menus próprios: o filtro central exige tanto o
      // módulo habilitado quanto a permissão de visualização correspondente.
      { titleKey: 'nav.categories.retention', defaultLabel: 'Retenção', url: '/tenant/retention/dashboard', menuKey: 'retention_dashboard', requiresModule: 'retention', icon: HeartHandshake },
      { titleKey: 'nav.categories.legal', defaultLabel: 'Jurídico', url: '/tenant/legal/dashboard', menuKey: 'legal_dashboard', requiresModule: 'legal', icon: Scale },
      { titleKey: 'nav.categories.vehicles', defaultLabel: 'Veículos', url: '/tenant/vehicles/dashboard', menuKey: 'vehicles_list', requiresModule: 'vehicles', icon: Car },
      // 2026-10-19: Dashboard da O.S. também no hub geral de Dashboards,
      // com a mesma flag de módulo e menuKey do menu próprio de Ordem de Serviço.
      { titleKey: 'nav.categories.serviceOrders', defaultLabel: 'O.S.', url: '/tenant/service-orders/dashboard', menuKey: 'os_list', requiresModule: 'serviceOrders', icon: Wrench },
    ],
  },


  // ---------------------------------------------------------------- Conversas
  {
    id: 'conversations',
    titleKey: 'tenant.items.conversas',
    defaultLabel: 'Conversas',
    icon: MessagesSquare,
    moduleType: 'conversations',
    matchPrefixes: [
      '/tenant/queue',
      '/tenant/scheduled-messages',
      '/tenant/inboxes',
      '/tenant/templates',
      '/tenant/reminders',
    ],
    children: [
      { titleKey: 'tenant.items.filaAtendimento', defaultLabel: 'Fila de Atendimento', url: '/tenant/queue', menuKey: 'queue', icon: ListTodo },
      { titleKey: 'tenant.items.mensagensAgendadas', defaultLabel: 'Mensagens Agendadas', url: '/tenant/scheduled-messages', menuKey: 'scheduled_messages', icon: CalendarClock },
      { titleKey: 'tenant.items.inboxes', defaultLabel: 'Inboxes', url: '/tenant/inboxes', menuKey: 'inboxes', icon: MessageSquare },
      { titleKey: 'tenant.items.templates', defaultLabel: 'Templates', url: '/tenant/templates', menuKey: 'templates', icon: LayoutTemplate },
      { titleKey: 'tenant.items.lembretes', defaultLabel: 'Central de Lembretes', url: '/tenant/reminders', menuKey: 'reminders_center', icon: Bell },
    ],
  },

  // ------------------------------------------------------------------- Kanban
  // Categoria dedicada aos kanbans: conversas + pipelines dos workspaces do CRM
  // (a árvore de workspaces mantém o visual anterior da CrmSidebar).
  {
    id: 'kanban',
    titleKey: 'tenant.items.kanban',
    defaultLabel: 'Kanban',
    icon: Kanban,
    matchPrefixes: ['/tenant/kanban', '/tenant/crm/pipeline'],
    specialSection: 'crmWorkspaces',
    // A árvore de Workspaces → Pipelines leva para /tenant/crm/pipeline,
    // rota protegida por `crm_deals`: só exibe para quem tem essa permissão.
    specialSectionMenuKey: 'crm_deals',
    specialSectionModule: 'crm',
    // CHANGELOG:
    //   RELEASE: 2026-08-25 v1.6.0 - Cada atalho de kanban passa a usar o ícone do
    //            módulo correspondente (antes todos usavam o ícone genérico Kanban),
    //            facilitando a identificação visual no painel de submenus.
    //   RELEASE: 2026-08-25 v1.4.0 - Adicionados atalhos para os demais kanbans do
    //            sistema (Propostas, Contratos, Tarefas, Retenção, Jurídico).
    //            Reaproveitam as menuKeys/permissões já existentes de cada tela e
    //            respeitam o módulo do tenant via `requiresModule`.
    children: [
      { titleKey: 'tenant.items.kanbanConversas', defaultLabel: 'Kanban de Conversas', url: '/tenant/kanban', menuKey: 'kanban', icon: MessagesSquare },
      // Atalhos (cópias) dos kanbans existentes — nenhuma tela nova é criada
      { titleKey: 'tenant.items.kanbanPropostas', defaultLabel: 'Kanban de Propostas', url: '/tenant/crm/proposals/kanban', menuKey: 'crm_proposals_kanban', icon: FileText, requiresModule: 'crm' },
      // Kanban de Contratos = modo "kanban" da tela de contratos gerados (?view=kanban)
      { titleKey: 'tenant.items.kanbanContratos', defaultLabel: 'Kanban de Contratos', url: '/tenant/crm/generated-contracts?view=kanban', menuKey: 'crm_contracts', icon: FileCheck, requiresModule: 'crm' },
      { titleKey: 'tenant.items.kanbanTarefas', defaultLabel: 'Kanban de Tarefas', url: '/tenant/tasks/kanban', menuKey: 'tasks_kanban', icon: ListChecks, requiresModule: 'tasks' },
      { titleKey: 'tenant.items.kanbanRetencao', defaultLabel: 'Kanban de Retenção', url: '/tenant/retention/kanban', menuKey: 'retention_kanban', icon: HeartHandshake, requiresModule: 'retention' },
      // 2026-10-16: atalho do Kanban de Remessas (Logística)
      { titleKey: 'nav.items.logisticsShipmentsKanban', defaultLabel: 'Kanban de Remessas', url: '/tenant/logistics/shipments/kanban', menuKey: 'log_shipments', icon: Truck },
      { titleKey: 'tenant.items.kanbanJuridico', defaultLabel: 'Kanban Jurídico', url: '/tenant/legal/kanban', menuKey: 'legal_kanban', icon: Scale, requiresModule: 'legal' },
      // v1.5.0 (2026-09-26): atalho do Kanban Vistoria (módulos Veículos + Checklist)
      { titleKey: 'tenant.items.kanbanVistoria', defaultLabel: 'Kanban Vistoria', url: '/tenant/vehicles/inspections/kanban', menuKey: 'vehicles_inspections_kanban', icon: ClipboardCheck, requiresModule: 'checklist' },


    ],
  },



  // --------------------------------------------------------------------- CRM
  {
    id: 'crm',
    titleKey: 'nav.categories.deals',
    defaultLabel: 'CRM',
    icon: Briefcase,
    moduleType: 'crm',
    requiresModule: 'crm',
    // Agendamento Online passou de Atividades para CRM (v1.2.0)
    matchPrefixes: ['/tenant/crm', '/tenant/scheduling'],
    children: [
      { titleKey: 'crm.items.dashboard', defaultLabel: 'Dashboard', url: '/tenant/crm', menuKey: 'crm_dashboard', icon: LayoutDashboard },
      { titleKey: 'crm.items.deals', defaultLabel: 'Todos os Negócios', url: '/tenant/crm/deals', menuKey: 'crm_deals', icon: Briefcase },
      // v1.5.0 (2026-08-25): grupo expansível "Propostas" virou item simples
      // "Propostas Geradas" (o Kanban de Propostas segue no menu pai Kanban).
      { titleKey: 'crm.items.proposalsList', defaultLabel: 'Propostas Geradas', url: '/tenant/crm/proposals', menuKey: 'crm_proposals_list', icon: FileText },
      { titleKey: 'crm.items.generatedContracts', defaultLabel: 'Contratos Gerados', url: '/tenant/crm/generated-contracts', menuKey: 'crm_contracts', icon: FileCheck },
      { titleKey: 'crm.items.sellers', defaultLabel: 'Vendedores', url: '/tenant/crm/sellers', menuKey: 'crm_sellers', icon: UserCheck },
      { titleKey: 'crm.items.goals', defaultLabel: 'Metas', url: '/tenant/crm/goals', menuKey: 'crm_goals', icon: Target },
      // Movido de Atividades para CRM (v1.2.0)
      { titleKey: 'tenant.items.agendamento', defaultLabel: 'Agendamento Online', url: '/tenant/scheduling', menuKey: 'scheduling', icon: CalendarClock },

      // ------------------------------------------------ Seção: Configurações
      // v1.5.0: separa os cadastros/parâmetros do bloco operacional acima.
      { sectionTitle: 'Configurações', titleKey: 'crm.items.categories', defaultLabel: 'Categorias', url: '/tenant/crm/categories', menuKey: 'crm_categories', icon: FolderTree },
      { titleKey: 'crm.items.products', defaultLabel: 'Produtos', url: '/tenant/crm/products', menuKey: 'crm_products', icon: Package },
      { titleKey: 'crm.items.priceTables', defaultLabel: 'Tabelas de Preço', url: '/tenant/crm/price-tables', menuKey: 'crm_prices', icon: DollarSign },
      { titleKey: 'crm.items.Template Contratos', defaultLabel: 'Modelos de Contrato', url: '/tenant/crm/contract-templates', menuKey: 'crm_contract_templates', icon: FileSignature },
      { titleKey: 'crm.items.contracts', defaultLabel: 'Contratos', url: '/tenant/crm/contracts', menuKey: 'crm_contracts', icon: FileSignature },
      { titleKey: 'crm.items.documentTypes', defaultLabel: 'Tipos de Documento', url: '/tenant/crm/document-types', menuKey: 'crm_document_types', icon: FileCheck },
      { titleKey: 'crm.items.workspaces', defaultLabel: 'Workspaces', url: '/tenant/crm/workspaces', menuKey: 'crm_workspaces', icon: Layers },
      { titleKey: 'crm.items.leadSources', defaultLabel: 'Origens de Lead', url: '/tenant/crm/lead-sources', menuKey: 'crm_param_lead_sources', icon: Megaphone },
      { titleKey: 'crm.items.lossReasons', defaultLabel: 'Motivos de Perda', url: '/tenant/crm/loss-reasons', menuKey: 'crm_param_loss_reasons', icon: XCircle },
      { titleKey: 'crm.items.proposalTypes', defaultLabel: 'Tipos de Proposta', url: '/tenant/crm/proposal-types', menuKey: 'crm_param_proposal_types', icon: FileText },
    ],
  },

  // ------------------------------------------------------------------ Pessoas
  // 2026-10-06: menu transversal (não depende do módulo Estoque). Contatos e
  // Clientes viraram submenus; tipos especialistas abrem /tenant/persons/:role.
  {
    id: 'persons',
    titleKey: 'nav.categories.persons',
    defaultLabel: 'Pessoas',
    icon: Contact,
    matchPrefixes: ['/tenant/persons', '/tenant/contacts', '/tenant/crm/customers'],
    children: [
      { titleKey: 'tenant.items.contatos', defaultLabel: 'Contatos', url: '/tenant/contacts', menuKey: 'contacts', icon: Contact },
      { titleKey: 'tenant.items.clientes', defaultLabel: 'Clientes', url: '/tenant/crm/customers', menuKey: 'crm_customers', icon: Users },
      { titleKey: 'nav.items.personsSuppliers', defaultLabel: 'Fornecedores', url: '/tenant/persons/fornecedores', menuKey: 'persons_suppliers', icon: Store },
      { titleKey: 'nav.items.personsManufacturers', defaultLabel: 'Fabricantes', url: '/tenant/persons/fabricantes', menuKey: 'persons_manufacturers', icon: Factory },
      { titleKey: 'nav.items.personsProviders', defaultLabel: 'Prestadores', url: '/tenant/persons/prestadores', menuKey: 'persons_providers', icon: Wrench },
      { titleKey: 'nav.items.personsCarriers', defaultLabel: 'Transportadoras', url: '/tenant/persons/transportadoras', menuKey: 'persons_carriers', icon: Truck },
    ],
  },

  // ---------------------------------------------------------------- Logística
  // 2026-10-16: módulo transversal de remessas (Fase 2). Permissão própria `log_shipments`.
  {
    id: 'logistics',
    titleKey: 'nav.categories.logistics',
    defaultLabel: 'Logística',
    icon: Truck,
    matchPrefixes: ['/tenant/logistics'],
    children: [
      { titleKey: 'nav.items.logisticsShipments', defaultLabel: 'Remessas', url: '/tenant/logistics/shipments', menuKey: 'log_shipments', icon: Truck },
      // 2026-10-16: Kanban de Remessas (somente leitura)
      { titleKey: 'nav.items.logisticsShipmentsKanban', defaultLabel: 'Kanban de Remessas', url: '/tenant/logistics/shipments/kanban', menuKey: 'log_shipments', icon: Truck },
    ],
  },

  // -------------------------------------------------------- Disparo Campanhas
  {
    id: 'campaigns',
    titleKey: 'tenant.items.campanhas',
    defaultLabel: 'Disparo Campanhas',
    icon: Megaphone,
    matchPrefixes: ['/tenant/campaigns', '/tenant/lists'],
    children: [
      { titleKey: 'nav.items.whatsapp', defaultLabel: 'WhatsApp', url: '/tenant/campaigns', menuKey: 'campaigns', icon: MessageSquare },
      // Importação de Listas passou a ser submenu de Campanhas (v1.1.0)
      { titleKey: 'tenant.items.listas', defaultLabel: 'Importação de Listas', url: '/tenant/lists', menuKey: 'lists_contacts', icon: FileSpreadsheet },
    ],
  },

  // --------------------------------------------------------------- Calendário
  {
    id: 'calendar',
    titleKey: 'nav.categories.calendar',
    defaultLabel: 'Calendário',
    icon: CalendarDays,
    url: '/tenant/calendar',
    menuKey: null,
    matchPrefixes: ['/tenant/calendar'],
  },

  // ------------------------------------------------------------------ Tarefas
  {
    id: 'tasks',
    titleKey: 'tasks.title',
    defaultLabel: 'Tarefas',
    icon: ListChecks,
    moduleType: 'tasks',
    requiresModule: 'tasks',
    matchPrefixes: ['/tenant/tasks'],
    // A seção dinâmica reutiliza a mesma permissão do Kanban e o catálogo
    // oficial de categorias ativas do módulo Tarefas.
    specialSection: 'taskCategories',
    specialSectionMenuKey: 'tasks_kanban',
    specialSectionModule: 'tasks',
    specialSectionTaskType: 'normal',
    children: [
      { titleKey: 'tasks.items.dashboard', defaultLabel: 'Dashboard', url: '/tenant/tasks/dashboard', menuKey: 'tasks_dashboard', icon: LayoutDashboard },
      { titleKey: 'tasks.items.list', defaultLabel: 'Listagem', url: '/tenant/tasks/list', menuKey: 'tasks_list', icon: ListTodo },
      { titleKey: 'tasks.items.categories', defaultLabel: 'Categorias', url: '/tenant/tasks/categories', menuKey: 'tasks_categories', icon: FolderTree },
      { titleKey: 'tasks.items.phases', defaultLabel: 'Fases', url: '/tenant/tasks/phases', menuKey: 'tasks_phases', icon: Columns },
      { titleKey: 'tasks.items.classifications', defaultLabel: 'Classificações', url: '/tenant/tasks/classifications', menuKey: 'tasks_classifications', icon: Tags },
      { titleKey: 'tasks.items.checklist_templates', defaultLabel: 'Modelos de Checklist', url: '/tenant/tasks/checklist-templates', menuKey: 'tasks_checklist_templates', icon: ListChecks },
      // Atalho para as permissões por fase (redireciona para a aba unificada)
      { titleKey: 'tasks.items.phase_permissions', defaultLabel: 'Permissões Por Fase', url: '/tenant/tasks/phase-permissions', menuKey: 'tasks_phases', icon: Shield },

    ],
  },

  // ---------------------------------------------------------------- Retenção
  // Reaproveita 100% as telas de Tarefas; o escopo dos dados é o task_type
  // 'retention', derivado do prefixo da rota (/tenant/retention).
  {
    id: 'retention',
    titleKey: 'nav.categories.retention',
    defaultLabel: 'Retenção',
    icon: HeartHandshake,
    moduleType: 'tasks',
    requiresModule: 'retention',
    matchPrefixes: ['/tenant/retention'],
    specialSection: 'taskCategories',
    specialSectionMenuKey: 'retention_kanban',
    specialSectionModule: 'retention',
    specialSectionTaskType: 'retention',
    children: [
      { titleKey: 'nav.items.retentionDashboard', defaultLabel: 'Dashboard', url: '/tenant/retention/dashboard', menuKey: 'retention_dashboard', icon: LayoutDashboard },
      { titleKey: 'nav.items.retentionList', defaultLabel: 'Listagem', url: '/tenant/retention/list', menuKey: 'retention_list', icon: ListTodo },
      { titleKey: 'nav.items.retentionCategories', defaultLabel: 'Categorias', url: '/tenant/retention/categories', menuKey: 'retention_categories', icon: FolderTree },
      { titleKey: 'nav.items.retentionPhases', defaultLabel: 'Fases', url: '/tenant/retention/phases', menuKey: 'retention_phases', icon: Columns },
      { titleKey: 'nav.items.retentionClassifications', defaultLabel: 'Classificações', url: '/tenant/retention/classifications', menuKey: 'retention_classifications', icon: Tags },
      // Motivos exigidos ao concluir uma solicitação de Retenção
      { titleKey: 'nav.items.retentionCompletionReasons', defaultLabel: 'Motivos de Conclusão', url: '/tenant/retention/completion-reasons', menuKey: 'retention_completion_reasons', icon: ListChecks },
      // Permissões por fase escopadas ao módulo (abre a subaba Retenção)
      { titleKey: 'nav.items.retentionPhasePermissions', defaultLabel: 'Permissões Por Fase', url: '/tenant/retention/phase-permissions', menuKey: 'retention_phases', icon: Shield },
    ],
  },

  // ---------------------------------------------------------------- Jurídico
  // Mesma base de Tarefas, escopo task_type 'legal' (/tenant/legal).
  {
    id: 'legal',
    titleKey: 'nav.categories.legal',
    defaultLabel: 'Jurídico',
    icon: Scale,
    moduleType: 'tasks',
    requiresModule: 'legal',
    matchPrefixes: ['/tenant/legal'],
    specialSection: 'taskCategories',
    specialSectionMenuKey: 'legal_kanban',
    specialSectionModule: 'legal',
    specialSectionTaskType: 'legal',
    children: [
      { titleKey: 'nav.items.legalDashboard', defaultLabel: 'Dashboard', url: '/tenant/legal/dashboard', menuKey: 'legal_dashboard', icon: LayoutDashboard },
      { titleKey: 'nav.items.legalList', defaultLabel: 'Listagem', url: '/tenant/legal/list', menuKey: 'legal_list', icon: ListTodo },
      { titleKey: 'nav.items.legalCategories', defaultLabel: 'Categorias', url: '/tenant/legal/categories', menuKey: 'legal_categories', icon: FolderTree },
      { titleKey: 'nav.items.legalPhases', defaultLabel: 'Fases', url: '/tenant/legal/phases', menuKey: 'legal_phases', icon: Columns },
      { titleKey: 'nav.items.legalClassifications', defaultLabel: 'Classificações', url: '/tenant/legal/classifications', menuKey: 'legal_classifications', icon: Tags },
      // Motivos exigidos ao concluir um processo Jurídico
      { titleKey: 'nav.items.legalCompletionReasons', defaultLabel: 'Motivos de Conclusão', url: '/tenant/legal/completion-reasons', menuKey: 'legal_completion_reasons', icon: ListChecks },
      // Permissões por fase escopadas ao módulo (abre a subaba Jurídico)
      { titleKey: 'nav.items.legalPhasePermissions', defaultLabel: 'Permissões Por Fase', url: '/tenant/legal/phase-permissions', menuKey: 'legal_phases', icon: Shield },
    ],
  },

  // ------------------------------------------------------------------ Veículos
  // Só aparece quando o parâmetro "Habilitar módulo de veículos" está ligado.
  {
    id: 'vehicles',
    titleKey: 'nav.categories.vehicles',
    defaultLabel: 'Veículos',
    icon: Car,
    requiresModule: 'vehicles',
    matchPrefixes: ['/tenant/vehicles'],
    children: [
      // 2026-10-03: Dashboard do módulo Veículos
      { titleKey: 'nav.items.vehiclesDashboard', defaultLabel: 'Dashboard Veículos', url: '/tenant/vehicles/dashboard', menuKey: 'vehicles_list', icon: LayoutDashboard },
      { titleKey: 'nav.items.vehiclesList', defaultLabel: 'Lista Veículos', url: '/tenant/vehicles/list', menuKey: 'vehicles_list', icon: Car },
      // v1.1.0 (2026-08-12): teste de embed do sistema de rastreamento em iframe
      { titleKey: 'nav.items.vehiclesMap', defaultLabel: 'Mapa de Veículos', url: '/tenant/vehicles/map', menuKey: 'vehicles_list', icon: MapPin },
      { titleKey: 'nav.items.vehiclesCategories', defaultLabel: 'Categorias Veículos', url: '/tenant/vehicles/categories', menuKey: 'vehicles_categories', icon: Tags },
      // 2026-10-02: Status de veículo (sistema + personalizados)
      { titleKey: 'nav.items.vehiclesStatuses', defaultLabel: 'Status Veículos', url: '/tenant/vehicles/statuses', menuKey: 'vehicles_categories', icon: Tags },
      // v1.2.0 (2026-09-26): grupo "Vistoria" — central de vistorias (lista + kanban)
      { titleKey: 'nav.items.inspectionsGroup', defaultLabel: 'Vistoria', menuKey: null, icon: ClipboardCheck, requiresModule: 'checklist', children: [
        { titleKey: 'nav.items.inspectionsList', defaultLabel: 'Vistorias', url: '/tenant/vehicles/inspections', menuKey: 'vehicles_inspections', icon: ClipboardCheck, requiresModule: 'checklist' },
        { titleKey: 'nav.items.inspectionsKanban', defaultLabel: 'Kanban Vistoria', url: '/tenant/vehicles/inspections/kanban', menuKey: 'vehicles_inspections_kanban', icon: Kanban, requiresModule: 'checklist' },
      ] },
    ],
  },

  // 2026-10-10: categoria visual Gestão Rastreador — mantém a flag interna stock_enabled.
  {
    id: 'stock',
    titleKey: 'nav.categories.stock',
    defaultLabel: 'Gestão Rastreador',
    icon: Boxes,
    requiresModule: 'stock',
    matchPrefixes: ['/tenant/stock'],
    children: [
      // Fases 4/5: Visão Geral e Movimentações
      { titleKey: 'nav.items.stockOverview', defaultLabel: 'Visão Geral', url: '/tenant/stock/overview', menuKey: 'stock_overview', icon: LayoutDashboard },
      { titleKey: 'nav.items.stockMovements', defaultLabel: 'Movimentações', url: '/tenant/stock/movements', menuKey: 'stock_movements', icon: Route },
      // Fase 3: itens controlados individualmente (IMEI / ICCID / código do kit)
      { titleKey: 'nav.items.stockTrackers', defaultLabel: 'Rastreadores', url: '/tenant/stock/trackers', menuKey: 'stock_trackers', icon: Cpu },
      { titleKey: 'nav.items.stockKits', defaultLabel: 'Kits', url: '/tenant/stock/kits', menuKey: 'stock_kits', icon: Package },
      { titleKey: 'nav.items.stockLocations', defaultLabel: 'Endereços de Estoque', url: '/tenant/stock/locations', menuKey: 'stock_locations', icon: MapPin },
      // 2026-10-11: Homologação de rastreadores
      { titleKey: 'nav.items.stockHomologations', defaultLabel: 'Homologação', url: '/tenant/stock/homologations', menuKey: 'stock_homologations', icon: BadgeCheck },
      // 2026-10-11: Plataformas de rastreamento
      { titleKey: 'nav.items.stockPlatforms', defaultLabel: 'Plataformas', url: '/tenant/stock/platforms', menuKey: 'stock_platforms', icon: Monitor },
      { titleKey: 'nav.items.stockModels', defaultLabel: 'Modelos de Rastreador', url: '/tenant/stock/models', menuKey: 'stock_models', icon: Cpu },
      // 2026-10-10: Gestão de Chip permanece agrupada e passa ao fim do módulo.
      { titleKey: 'nav.items.stockChipGroup', defaultLabel: 'Gestão de Chip', menuKey: null, icon: Layers, children: [
        { titleKey: 'nav.items.stockChips', defaultLabel: 'Chips', url: '/tenant/stock/chips', menuKey: 'stock_chips', icon: Layers },
        { titleKey: 'nav.items.stockDataPlans', defaultLabel: 'Planos de dados', url: '/tenant/stock/data-plans', menuKey: 'stock_data_plans', icon: Route },
        // 2026-10-14: Cancelamento de chips em lote junto ao broker
        { titleKey: 'nav.items.stockChipCancellations', defaultLabel: 'Cancelamento', url: '/tenant/stock/chip-cancellations', menuKey: 'stock_chip_cancellations', icon: Ban },
      ] },
    ],
  },

  // 2026-10-08 (Fase 1 da OS): categoria Ordem de Serviço — só com service_orders_enabled.
  {
    id: 'serviceOrders',
    titleKey: 'nav.categories.serviceOrders',
    defaultLabel: 'Ordem de Serviço',
    icon: Wrench,
    requiresModule: 'serviceOrders',
    matchPrefixes: ['/tenant/service-orders'],
    // Reutiliza o catálogo oficial de categorias da O.S. no mesmo padrão de Tarefas.
    specialSection: 'serviceOrderCategories',
    specialSectionMenuKey: 'os_kanban',
    specialSectionModule: 'serviceOrders',
    children: [
      { titleKey: 'nav.items.serviceOrdersDashboard', defaultLabel: 'Dashboard O.S.', url: '/tenant/service-orders/dashboard', menuKey: 'os_list', icon: LayoutDashboard },
      { titleKey: 'nav.items.serviceOrdersList', defaultLabel: 'Ordens de Serviço', url: '/tenant/service-orders/list', menuKey: 'os_list', icon: List },
      { titleKey: 'nav.items.serviceOrdersCategories', defaultLabel: 'Categorias de OS', url: '/tenant/service-orders/categories', menuKey: 'os_categories', icon: Tags },
      { titleKey: 'nav.items.serviceOrdersPhases', defaultLabel: 'Fases de OS', url: '/tenant/service-orders/phases', menuKey: 'os_phases', icon: Layers },
      { titleKey: 'nav.items.serviceOrdersPhasePermissions', defaultLabel: 'Permissões Por Fase', url: '/tenant/service-orders/phase-permissions', menuKey: 'os_phases', icon: Shield },
      { titleKey: 'nav.items.serviceOrdersTypes', defaultLabel: 'Tipos de Serviço', url: '/tenant/service-orders/service-types', menuKey: 'os_service_types', icon: Wrench },
    ],
  },

  // v1.1.0 (2026-08-23): a categoria "Inteligência de Dados" deixou de existir
  // no rail — seus itens viraram um GRUPO (nível 3) dentro de Configurações.





  // --------------------------------------------------------------- Atividades
  {
    id: 'activities',
    titleKey: 'crm.items.activities',
    defaultLabel: 'Atividades',
    icon: ActivitySquare,
    moduleType: 'activities',
    requiresModule: 'activities',
    matchPrefixes: ['/tenant/activities'],
    children: [
      { titleKey: 'activities.items.dashboard', defaultLabel: 'Dashboard', url: '/tenant/activities/dashboard', menuKey: 'activities_list', icon: LayoutDashboard },
      { titleKey: 'activities.items.list', defaultLabel: 'Listagem Atividades', url: '/tenant/activities/list', menuKey: 'activities_list', icon: ListChecks },
      { titleKey: 'crm.items.activitiesFeed', defaultLabel: 'Feed Atividades', url: '/tenant/crm/activities', menuKey: 'crm_deals', icon: ListChecks },
      { titleKey: 'activities.items.calendar', defaultLabel: 'Agenda', url: '/tenant/crm/activities/calendar', menuKey: 'crm_deals', icon: CalendarDays },
    ],
  },

  // ---------------------------------------------------------------- Financeiro
  {
    id: 'financial',
    titleKey: 'admin.items.financeiro',
    defaultLabel: 'Financeiro',
    icon: DollarSign,
    moduleType: 'financial',
    requiresModule: 'financial',
    matchPrefixes: ['/tenant/financial'],
    children: [
      { titleKey: 'financial.items.dashboard', defaultLabel: 'Dashboard', url: '/tenant/financial/dashboard', menuKey: 'financial_dashboard', icon: LayoutDashboard },
      { titleKey: 'financial.items.charges', defaultLabel: 'Cobranças', url: '/tenant/financial/charges', menuKey: 'financial_charges', icon: DollarSign },
      { titleKey: 'financial.items.installments', defaultLabel: 'Parcelas', url: '/tenant/financial/installments', menuKey: 'financial_charges', icon: CreditCard },
      { titleKey: 'financial.items.subscriptions', defaultLabel: 'Assinaturas', url: '/tenant/financial/subscriptions', menuKey: 'financial_charges', icon: CalendarClock },
      { titleKey: 'financial.items.paymentLinks', defaultLabel: 'Links de Pagamento', url: '/tenant/financial/payment-links', menuKey: 'financial_charges', icon: Globe },
      { titleKey: 'financial.items.billingReminders', defaultLabel: 'Réguas de Cobrança', url: '/tenant/financial/billing-reminders', menuKey: 'financial_billing_reminders', icon: Bell },
      { titleKey: 'financial.items.accounts', defaultLabel: 'Contas', url: '/tenant/financial/accounts', menuKey: 'financial_accounts', icon: Building2 },
      { titleKey: 'financial.items.paymentMethods', defaultLabel: 'Meios de Pagamento', url: '/tenant/financial/payment-methods', menuKey: 'financial_payment_methods', icon: CreditCard },
    ],
  },

  // --------------------------------------------------------------- Indicações
  {
    id: 'referral',
    titleKey: 'nav.categories.referral',
    defaultLabel: 'Indicações',
    icon: Handshake,
    moduleType: 'referral',
    requiresModule: 'referral',
    matchPrefixes: ['/tenant/indicacao'],
    children: [
      { titleKey: 'referral.items.dashboard', defaultLabel: 'Dashboard', url: '/tenant/indicacao', menuKey: 'referral_dashboard', icon: LayoutDashboard },
      { titleKey: 'referral.items.affiliates', defaultLabel: 'Afiliados', url: '/tenant/indicacao/afiliados', menuKey: 'referral_affiliates', icon: Users },
      { titleKey: 'referral.items.referrals', defaultLabel: 'Indicações', url: '/tenant/indicacao/indicacoes', menuKey: 'referral_referrals', icon: Handshake },
      { titleKey: 'referral.items.commissions', defaultLabel: 'Comissões', url: '/tenant/indicacao/comissoes', menuKey: 'referral_commissions', icon: DollarSign },
      { titleKey: 'referral.items.payouts', defaultLabel: 'Pagamentos', url: '/tenant/indicacao/pagamentos', menuKey: 'referral_payouts', icon: CreditCard },
      { titleKey: 'referral.items.programs', defaultLabel: 'Programas', url: '/tenant/indicacao/programas', menuKey: 'referral_programs', icon: Layers },
      { titleKey: 'referral.items.campaigns', defaultLabel: 'Campanhas', url: '/tenant/indicacao/campanhas', menuKey: 'referral_campaigns', icon: Megaphone },
      { titleKey: 'referral.items.materials', defaultLabel: 'Materiais', url: '/tenant/indicacao/materiais', menuKey: 'referral_materials', icon: FileSpreadsheet },
      { titleKey: 'referral.items.contractTemplates', defaultLabel: 'Modelos de Contrato', url: '/tenant/indicacao/modelos-contrato', menuKey: 'crm_contract_templates', icon: FileSignature },
    ],
  },

  // --------------------------------------------------------------------- Wiki
  {
    id: 'knowledge',
    titleKey: 'nav.categories.knowledge',
    defaultLabel: 'Wiki',
    icon: BookOpen,
    requiresModule: 'knowledge',
    matchPrefixes: ['/tenant/kb'],
    children: [
      { titleKey: 'nav.items.kbSpaces', defaultLabel: 'Espaços', url: '/tenant/kb', menuKey: 'kb_view', icon: BookOpen },
      { titleKey: 'nav.items.kbImport', defaultLabel: 'Importar', url: '/tenant/kb/import', menuKey: 'kb_admin', icon: Upload },
      { titleKey: 'nav.items.kbPermissions', defaultLabel: 'Permissões', url: '/tenant/kb/permissions', menuKey: 'kb_admin', icon: Shield },
      { titleKey: 'nav.items.kbExtranet', defaultLabel: 'Extranet', url: '/tenant/kb/extranet', menuKey: 'kb_admin', icon: Globe },
    ],
  },

  // --------------------------------------------------------------- Automações
  {
    id: 'automations',
    titleKey: 'nav.categories.automations',
    defaultLabel: 'Automações',
    icon: Workflow,
    matchPrefixes: ['/tenant/routing', '/tenant/flows', '/tenant/crm/playbooks', '/tenant/workflow'],
    children: [
      { titleKey: 'tenant.items.regrasRoteamento', defaultLabel: 'Ações rápidas/Macros', url: '/tenant/routing', menuKey: 'routing', icon: Route },
      { titleKey: 'tenant.items.fluxos', defaultLabel: 'Fluxos', url: '/tenant/flows', menuKey: 'flows', icon: GitBranch },
      // Playbook de Vendas pertence ao CRM: só aparece com o módulo contratado
      { titleKey: 'crm.items.playbooks', defaultLabel: 'Playbook de Vendas', url: '/tenant/crm/playbooks', menuKey: 'crm_playbooks', requiresModule: 'crm', icon: ListChecks },
      // Workflow (esteira comercial): submenu só aparece quando a flag
      // `workflow_enabled` do tenant está ligada (Configurações › Workflow)
      // 2026-10-06: Workflow vira grupo com submenus, cada um com permissão própria
      { titleKey: 'tenant.items.workflow', defaultLabel: 'Workflow', menuKey: null, requiresModule: 'workflow', icon: Workflow, children: [
        { titleKey: 'tenant.items.workflowProcesses', defaultLabel: 'Processos', url: '/tenant/workflow', menuKey: 'workflow_processes', requiresModule: 'workflow', icon: GitBranch },
        { titleKey: 'tenant.items.workflowPending', defaultLabel: 'Pendências', url: '/tenant/workflow/pending', menuKey: 'workflow_pending', requiresModule: 'workflow', icon: ClipboardCheck },
        { titleKey: 'tenant.items.workflowExecutions', defaultLabel: 'Execuções', url: '/tenant/workflow/executions', menuKey: 'workflow_executions', requiresModule: 'workflow', icon: ActivitySquare },
        { titleKey: 'tenant.items.workflowErrors', defaultLabel: 'Erros do processo', url: '/tenant/workflow/errors', menuKey: 'workflow_errors', requiresModule: 'workflow', icon: AlertOctagon },
      ] },
    ],
  },

  // Importação de Listas foi movida para dentro da categoria "Campanhas" (v1.1.0)


  // ------------------------------------------------------------ Configurações
  {
    id: 'settings',
    titleKey: 'tenant.items.configuracoes',
    defaultLabel: 'Configurações',
    icon: Settings,
    matchPrefixes: [
      '/tenant/departments',
      '/tenant/agents',
      '/tenant/company',
      '/tenant/plans',
      '/tenant/permissions',
      '/tenant/distribution',
      '/tenant/settings',
      // Content Studio (Modelos de Conteúdo) mora dentro de Configurações
      '/tenant/content',
      // v1.1.0: Inteligência de Dados passou a viver dentro de Configurações
      '/tenant/data',
    ],
    children: [
      { sectionTitle: 'Organização', titleKey: 'tenant.items.departamentos', defaultLabel: 'Departamentos', url: '/tenant/departments', menuKey: 'departments', icon: Network },
      { titleKey: 'tenant.items.agentes', defaultLabel: 'Agentes', url: '/tenant/agents', menuKey: 'agents', icon: Users },
      { titleKey: 'tenant.items.empresa', defaultLabel: 'Empresa', url: '/tenant/company', menuKey: 'company', icon: Building2 },
      { titleKey: 'tenant.items.planos', defaultLabel: 'Planos', url: '/tenant/plans', menuKey: 'company', icon: CreditCard },
      { sectionTitle: 'Acesso e atendimento', titleKey: 'tenant.items.permissoes', defaultLabel: 'Permissões', url: '/tenant/permissions', menuKey: 'settings', icon: Shield },
      { titleKey: 'tenant.items.filas', defaultLabel: 'Filas', url: '/tenant/distribution/queues', menuKey: 'distribution_queues', icon: Layers },
      { sectionTitle: 'Preferências do sistema', titleKey: 'tenant.items.configuracoes', defaultLabel: 'Configurações', url: '/tenant/settings', menuKey: 'settings', icon: Settings },
      // v1.2.0: auditoria de tudo que foi exportado do sistema (somente admin)
      { titleKey: 'tenant.items.exportacaoDados', defaultLabel: 'Exportação Dados', url: '/tenant/settings/exports', menuKey: 'settings', icon: FileDown },
      // Templates do Sistema: exporta/importa a configuração completa da conta
      { titleKey: 'tenant.items.templatesSistema', defaultLabel: 'Templates do Sistema', url: '/tenant/settings/system-templates', menuKey: 'settings', icon: Package },
      // Content Studio: modelos de documentos (propostas, páginas públicas, e-mails)
      { sectionTitle: 'Modelos e dados', titleKey: 'tenant.items.modelosConteudo', defaultLabel: 'Modelos de Conteúdo', url: '/tenant/content/templates', menuKey: 'settings', icon: FileText },
      // 2026-09-26: Template de Checklist (reutilizável por Vistoria e futuros módulos)
      { titleKey: 'tenant.items.templateChecklist', defaultLabel: 'Template de Checklist', url: '/tenant/settings/checklist-templates', menuKey: 'checklist_templates', requiresModule: 'checklist', icon: ClipboardList },
      // ------------------------------------------- Grupo: Inteligência de Dados
      // Item SEM `url`: funciona apenas como agrupador expansível (nível 3).
      {
        titleKey: 'nav.categories.data',
        defaultLabel: 'Inteligência de Dados',
        menuKey: null,
        icon: Database,
        children: [
          { titleKey: 'nav.items.dataConnections', defaultLabel: 'Conexões', url: '/tenant/data/connections', menuKey: 'data_connections', icon: Plug },
          // Dados Externos (Fase 2)
          { titleKey: 'nav.items.dataDatasets', defaultLabel: 'Datasets', url: '/tenant/data/datasets', menuKey: 'data_datasets', icon: Database },
          { titleKey: 'nav.items.dataExplorer', defaultLabel: 'Explorador de dados', url: '/tenant/data/explorer', menuKey: 'data_explorer', icon: Search },
          { titleKey: 'nav.items.dataSyncHistory', defaultLabel: 'Histórico de sincronização', url: '/tenant/data/sync-history', menuKey: 'data_sync_history', icon: History },
          // Fase 4 — lotes de ação criados a partir do Explorador/Comparação
          { titleKey: 'nav.items.dataActions', defaultLabel: 'Ações em lote', url: '/tenant/data/actions', menuKey: 'data_explorer', icon: Rocket },
        ],
      },
    ],

  },
];

/**
 * Ordem de exibição das categorias no rail (nível 1).
 * Ajuste aqui para reordenar o menu sem mover código.
 */
const RAIL_ORDER: string[] = [
  'dashboard',
  'chat',
  'crm',
  'kanban',
  'activities',
  'tasks',
  'retention',
  'legal',
  'financial',
  'conversations',
  'referral',
  'calendar',
  'persons',
  'vehicles',
  'campaigns',
  'knowledge',
  'automations',
  'settings',
];

/** Categorias ordenadas conforme RAIL_ORDER (não listadas vão para o fim). */
export const NAV_CATEGORIES: NavCategory[] = [...NAV_CATEGORIES_DEFS].sort((a, b) => {
  const ia = RAIL_ORDER.indexOf(a.id);
  const ib = RAIL_ORDER.indexOf(b.id);
  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
});
